import classNames from "classnames";
import { Sync } from "components/icons/Sync";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CaretDown } from "components/icons/CaretDown";
import { Field, FieldProps, Form, Formik } from "formik";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import dayjs from "dayjs";
import { FileArrowDown } from "components/icons/FileArrowDown";
import { SimpleSparkleGroup } from "components/icons/SimpleSparkleGroup";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { MultipleFiles } from "components/icons/MultipleFiles";
import { PlusIcon } from "components/icons/PlusIcon";
import { useState } from "react";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useSelector } from "react-redux";
import { getFilterStatus } from "store/selector/transactionFilter";
import { useLazyExportTxnQuery } from "store/apis/books";
import {
  useGetAllTransactionsDataQuery,
  useRunCategorizationEngineMutation,
} from "store/apis/transactions";
import { useSuggestTransactionsMutation } from "store/apis/invoices";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { openDownloadUrl } from "util/openDownloadUrl";
import { useModal } from "hooks/useModal";
import { AddBulkTransaction } from "./AddTransaction/AddBulkTransaction";
import { RefreshModal } from "./RefreshModal";
import { TransactionsBankSelector } from "./AddTransaction/TransactionsBankSelector";
import { AutoCategorizeConfirmationModal } from "./AutoCategorizeConfirmationModal";
import { useTransactionsFilter } from "hooks/useTransactionsFilter";
import { ShowControl } from "hooks/useShow";
import { SideChatControl } from "components/SideChatControl/SideChatControl";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { REFRESH_TYPE } from "constants/transaction";
import { SmartReview } from "./SmartReview/SmartReview";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ThreeDots from "static/images/ThreeDots.svg";
import { Padlock } from "components/icons/Padlock";
import { SparkleGreyOutlined } from "components/icons/SparkleGreyOutlined";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useWindowSize } from "hooks/useWindowSize";
import { useQuery } from "hooks/useQuery";
import { TXN_GROUP_BY } from "constants/bookkeeping";

type TransactionActionsProps = {
  sideChat: ShowControl;
  channelId?: string | null;
  isStripePage: boolean;
};

export const TransactionActions = ({
  sideChat,
  channelId,
  isStripePage,
}: TransactionActionsProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const { isMobile } = useWindowSize();
  const [isMobileRefreshOpen, setIsMobileRefreshOpen] = useState(false);
  const mobileRefreshState = isMobileRefreshOpen ? "open" : "closed";
  const [selectedCategorizationDate, setSelectedCategorizationDate] =
    useState<string>("");
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const { isAdmin } = useRoleBasedView();
  const { appliedFilterCount } = useSelector(getFilterStatus);
  const isFilterApplied = appliedFilterCount > 0;
  const [exportTxn, { isLoading: isExporting }] = useLazyExportTxnQuery();
  const [
    runCategorizationEngine,
    { isLoading: isRunningCategorizationEngine },
  ] = useRunCategorizationEngineMutation();
  const [suggestTransactions] = useSuggestTransactionsMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast, info } = useToast();
  const query = useQuery();
  const groupBy =
    query.get("group_by") === TXN_GROUP_BY.VENDOR ? TXN_GROUP_BY.VENDOR : null;

  const allFilters = useTransactionsFilter({
    searchTerm: null,
    pageNum: 1,
  });

  const { data: transactionData } = useGetAllTransactionsDataQuery(allFilters, {
    skip: !groupId || !entityId,
    refetchOnMountOrArgChange: true,
  });

  const { total_count = 0 } = transactionData || {};

  const addTransactionModal = useModal();
  const addTransactionManuallyModal = useModal();
  const refreshTransactionsModal = useModal();
  const refreshAttachmentsModal = useModal();
  const autoCategorizeConfirmationModal = useModal();
  const smartReviewModal = useModal();

  const onExport = async () => {
    try {
      const { download_url } = await exportTxn({
        ...allFilters,
        groupBy,
      }).unwrap();
      successToast({ message: "Transaction export successful" });
      openDownloadUrl(download_url);
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const onSuggestInvoices = async () => {
    if (!groupId || !entityId) return;
    try {
      await suggestTransactions({ groupId, entityId }).unwrap();
      info({
        title: "Refreshing",
        message:
          "Updating invoice suggestions. We'll notify you when they are ready",
      });
    } catch {
      alertToast({ message: "Error refreshing suggested invoices" });
    }
  };

  const startCategorizationEngine = async ({
    start_date,
  }: {
    start_date: string;
  }) => {
    setSelectedCategorizationDate(start_date);
    setIsDropdownOpen(false);
    autoCategorizeConfirmationModal.open();
  };

  const executeCategorizationEngine = async () => {
    try {
      if (entityId) {
        await runCategorizationEngine({
          entityId,
          start_date: selectedCategorizationDate,
        }).unwrap();
        successToast({
          message: "Auto categorization in progress.",
        });
        autoCategorizeConfirmationModal.close();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };
  const handleMobileRefreshToggle = (e: Event) => {
    e.preventDefault();
    setIsMobileRefreshOpen((isOpen) => !isOpen);
  };

  const refreshItems = (
    <>
      <Dropdown.Item
        disabled={!hasWritePermission}
        onSelect={refreshTransactionsModal.open}
        className="t-flex t-gap-2 t-items-center"
      >
        <span className="t-text-neutral">
          <Sync size="15" />
        </span>
        <span>Transactions</span>
      </Dropdown.Item>

      {!isStripePage && (
        <Dropdown.Item
          disabled={!hasWritePermission}
          onSelect={refreshAttachmentsModal.open}
          className="t-flex t-gap-2 t-items-center"
        >
          <FileArrowDown className="t-text-neutral" />
          <span>Attachments & Memos</span>
        </Dropdown.Item>
      )}

      <Dropdown.Item
        disabled={!hasWritePermission}
        onSelect={onSuggestInvoices}
        className="t-flex t-gap-2 t-items-center"
      >
        <SparkleGreyOutlined className="t-text-neutral" />
        <span>Suggested Invoices & Bills</span>
      </Dropdown.Item>
    </>
  );

  return (
    <div className="t-flex t-gap-2 t-ml-auto">
      <Dropdown.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <Dropdown.Trigger asChild>
          <Button
            customType="icon"
            size="xsmall"
            isLoading={isExporting}
            disabled={isExporting}
          >
            <img src={ThreeDots} alt="Options" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={4} align="end" className="!t-min-w-40">
            {!isMobile && (
              <Dropdown.Sub>
                <Dropdown.SubTrigger
                  disabled={!hasWritePermission}
                  icon={!hasWritePermission ? <Padlock /> : undefined}
                  className={!hasWritePermission ? "t-text-neutral-30" : ""}
                >
                  Refresh
                </Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent sideOffset={5}>
                    {refreshItems}

                    {isAdmin && (
                      <Dropdown.Sub>
                        <Dropdown.SubTrigger asChild>
                          <div className="t-flex t-gap-2 t-items-center t-cursor-pointer">
                            <span className="t-text-neutral">
                              <SimpleSparkleGroup />
                            </span>
                            <span className="t-mt-[1px]">Auto categorize</span>
                          </div>
                        </Dropdown.SubTrigger>
                        <Dropdown.Portal>
                          <Dropdown.SubContent
                            sideOffset={5}
                            className="t-bg-white t-shadow-md t-rounded-md t-z-50 !t-p-0 t-w-[17.5rem]"
                          >
                            <Formik initialValues={{}} onSubmit={() => {}}>
                              <Form className="reactDatePicker">
                                <Field name="categorizationEngineStartDate">
                                  {({ field }: FieldProps) => (
                                    <DateInput
                                      required
                                      {...field}
                                      portalId="categorizationEngineStartDate"
                                      maxDate={new Date()}
                                      onDateChange={(date) => {
                                        startCategorizationEngine({
                                          start_date:
                                            dayjs(date).format("YYYY-MM-DD"),
                                        });
                                      }}
                                      dateFormat="YYYY-MM-DD"
                                      inline
                                    />
                                  )}
                                </Field>
                              </Form>
                            </Formik>
                          </Dropdown.SubContent>
                        </Dropdown.Portal>
                      </Dropdown.Sub>
                    )}
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
            )}

            {/* On phones Refresh expands inline below itself instead of opening a side submenu. */}
            {isMobile && (
              <>
                <Dropdown.Item
                  disabled={!hasWritePermission}
                  icon={!hasWritePermission ? <Padlock /> : undefined}
                  onSelect={handleMobileRefreshToggle}
                  data-state={mobileRefreshState}
                  className={classNames(
                    "t-group t-flex t-items-center t-justify-between",
                    { "t-text-neutral-30": !hasWritePermission }
                  )}
                >
                  <span>Refresh</span>
                  <span className="t-flex t-transition-transform group-data-[state=open]:t-rotate-180">
                    <CaretDown />
                  </span>
                </Dropdown.Item>
                <div
                  data-state={mobileRefreshState}
                  className="t-hidden t-pl-3 data-[state=open]:t-block"
                >
                  {refreshItems}
                </div>
              </>
            )}

            {Boolean(total_count) && (
              <ConditionalToolTip
                condition={
                  isFilterApplied
                    ? `Export ${total_count} filtered transactions as a .CSV file`
                    : `Export ${total_count} transactions as a .CSV file`
                }
              >
                <Dropdown.Item onSelect={onExport}>Export</Dropdown.Item>
              </ConditionalToolTip>
            )}

            <Dropdown.Item
              onSelect={smartReviewModal.open}
              className="t-flex t-items-center t-gap-2"
              disabled={!hasWritePermission}
            >
              {!hasWritePermission && <Padlock />}
              <span>Smart review</span>
              <Tag tagType="blue" size="small" rounded icon={false}>
                New
              </Tag>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <LockedUi
        feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
        blockedUi={<LockedButton text="Add transaction" size="xsmall" />}
      >
        <Dropdown.Root>
          <Dropdown.Trigger asChild className="t-group">
            <span>
              <Button customType="primary" size="xsmall">
                {isSmallScreen ? "Add" : "Add transaction"}
                <span className="group-data-state-open:-t-rotate-180 group-data-state-closed:t-rotate-0 t-transform t-transition t-duration-300 t-ease-in-out t-ml-2">
                  <CaretDown />
                </span>
              </Button>
            </span>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content align="end" sideOffset={6}>
              <Dropdown.Item onSelect={addTransactionManuallyModal.open}>
                <PlusIcon size="12" />
                Add a transaction
              </Dropdown.Item>
              <Dropdown.Item onSelect={addTransactionModal.open}>
                <MultipleFiles />
                Add transactions in bulk
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </LockedUi>
      {!sideChat.shown && (
        <div className="t-flex t-items-center t-gap-3 t-ml-3 sm:-t-mr-2">
          <span className="t-bg-neutral-20 t-h-2/3 t-w-[1.5px]"></span>
          <SideChatControl
            channelId={channelId ? channelId : ""}
            open={sideChat.shown}
            onOpenChange={sideChat.toggle}
          />
        </div>
      )}
      <AddBulkTransaction
        close={addTransactionModal.close}
        isOpen={addTransactionModal.isOpen}
        openAddTransactionModal={addTransactionModal.open}
      />
      <RefreshModal
        isOpen={refreshTransactionsModal.isOpen}
        onClose={refreshTransactionsModal.close}
        type={REFRESH_TYPE.TRANSACTIONS}
      />
      <RefreshModal
        isOpen={refreshAttachmentsModal.isOpen}
        onClose={refreshAttachmentsModal.close}
        type={REFRESH_TYPE.ATTACHMENTS}
      />
      <TransactionsBankSelector
        isOpen={addTransactionManuallyModal.isOpen}
        close={addTransactionManuallyModal.close}
        onBack={addTransactionManuallyModal.close}
        step={1}
      />
      <AutoCategorizeConfirmationModal
        isOpen={autoCategorizeConfirmationModal.isOpen}
        onClose={autoCategorizeConfirmationModal.close}
        onConfirm={executeCategorizationEngine}
        isLoading={isRunningCategorizationEngine}
      />
      <SmartReview
        isOpen={smartReviewModal.isOpen}
        close={smartReviewModal.close}
      />
    </div>
  );
};
